import { useLayoutEffect, useRef, useState, useCallback } from "react";
import useResizeObserver from "use-resize-observer";

export const OverflowState = {
    Fits: 0,
    HideLabels: 1,
    CollapseMenu: 2
} as const;

type OverflowState = typeof OverflowState[keyof typeof OverflowState];

function getContextFromState(contextSize: number, state: OverflowState) {
    const base = new Array(Math.max(0, contextSize)).fill(null).map(() => ({
        overflow: false,
        overflowWidth: 0
    }));

    if (contextSize > 0 && state >= OverflowState.HideLabels) base[0].overflow = true;
    if (contextSize > 1 && state >= OverflowState.CollapseMenu) base[1].overflow = true;

    return base;
}

type HysteresisOptions = {
    /** pixels of deadband to stop Fit<->HideLabels flapping (default 30) */
    deadbandPx?: number;
    /** extra pixels to avoid scrollbar/rounding nonsense (default 1) */
    fudgePx?: number;
};

type MeasureState = {
    overflowState: OverflowState;
    /** clientWidth at the moment we escalated to HideLabels */
    hideLabelsWidth: number;
    /** clientWidth at the moment we escalated to CollapseMenu */
    collapseMenuWidth: number;
};

/**
 * Pure function: given previous state + current measurements, compute next state.
 *
 * The key insight: scrollWidth changes when we change state (hiding labels shrinks content).
 * So we can't use scrollWidth-vs-clientWidth with a deadband to decide whether to de-escalate.
 * Instead, we remember the clientWidth at which we escalated, and require the container to
 * grow beyond that + deadbandPx before de-escalating. This creates a dead zone:
 *
 *   ← collapsed ── escalateWidth ── dead zone ── (escalateWidth + deadband) ── expanded →
 */
export function computeNextState(
    prev: MeasureState,
    available: number,
    needed: number,
    deadbandPx: number,
    fudgePx: number
): MeasureState {
    const overflowing = needed > available + fudgePx;

    if (prev.overflowState === OverflowState.Fits) {
        if (overflowing) {
            return { overflowState: OverflowState.HideLabels, hideLabelsWidth: available, collapseMenuWidth: 0 };
        }
        return prev;
    }

    if (prev.overflowState === OverflowState.HideLabels) {
        if (overflowing) {
            return { ...prev, overflowState: OverflowState.CollapseMenu, collapseMenuWidth: available };
        }
        // De-escalate only if container is wider than where we collapsed + deadband
        if (available > prev.hideLabelsWidth + deadbandPx) {
            return { overflowState: OverflowState.Fits, hideLabelsWidth: 0, collapseMenuWidth: 0 };
        }
        return prev;
    }

    // CollapseMenu
    if (available > prev.collapseMenuWidth + deadbandPx) {
        // Relax one step — keep hideLabelsWidth so the next de-escalation check works
        return { ...prev, overflowState: OverflowState.HideLabels, collapseMenuWidth: 0 };
    }
    return prev;
}

export function useOverflowObserver(contextSize: number, opts: HysteresisOptions = {}) {
    const { deadbandPx = 30, fudgePx = 1 } = opts;

    const containerRef = useRef<HTMLElement | null>(null);
    const rafIdRef = useRef<number | null>(null);
    const measureStateRef = useRef<MeasureState>({
        overflowState: OverflowState.Fits,
        hideLabelsWidth: 0,
        collapseMenuWidth: 0,
    });

    const [overflowContext, setOverflowContext] = useState(() =>
        getContextFromState(contextSize, OverflowState.Fits)
    );

    // Keep context size synced
    useLayoutEffect(() => {
        measureStateRef.current = { overflowState: OverflowState.Fits, hideLabelsWidth: 0, collapseMenuWidth: 0 };
        setOverflowContext(getContextFromState(contextSize, OverflowState.Fits));
    }, [contextSize]);

    const scheduleMeasure = useCallback((fn: () => void) => {
        if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = requestAnimationFrame(() => {
            rafIdRef.current = null;
            fn();
        });
    }, []);

    const measureOnce = useCallback(() => {
        const el = containerRef.current;
        if (!el) return;

        const available = el.clientWidth;
        const needed = el.scrollWidth;

        const prev = measureStateRef.current;
        const next = computeNextState(prev, available, needed, deadbandPx, fudgePx);

        if (next !== prev) {
            measureStateRef.current = next;
            setOverflowContext(getContextFromState(contextSize, next.overflowState));
            // Re-measure after DOM changes settle (state change alters scrollWidth)
            scheduleMeasure(measureOnce);
        }
    }, [contextSize, deadbandPx, fudgePx, scheduleMeasure]);

    // Initial measure
    useLayoutEffect(() => {
        measureOnce();
        return () => {
            if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current);
        };
    }, [measureOnce]);

    // Resize observer
    useResizeObserver({
        ref: containerRef as any,
        onResize: () => scheduleMeasure(measureOnce)
    });

    const getOverflow = (index: number) => overflowContext[index]?.overflow ?? false;

    return {
        overflowContext,
        containerRef,
        getOverflow
    };
}
